import { useEffect, useMemo, useRef, useState } from "react";

import type { AggregatedDataset, AggregatedTopic, AvailableDate } from "@types";

export const RANGE_OPTIONS = [1, 3, 7, 14, 30] as const;
export type RangeOption = (typeof RANGE_OPTIONS)[number];
export type RangeSelection = RangeOption | "custom";

export interface DateRange {
  startDate: string;
  endDate: string;
}

interface DatasetState {
  data: AggregatedDataset | null;
  loading: boolean;
  error: string | null;
  availableDates: AvailableDate[];
}

type DatasetCache = Record<string, AggregatedDataset>;

function mergeTopics(topicsList: AggregatedTopic[]): AggregatedTopic[] {
  const map = new Map<string, AggregatedTopic>();
  for (const topic of topicsList) {
    const existing = map.get(topic.name);
    if (existing) {
      existing.paper_count += topic.paper_count;
      if (topic.last_fetched_at && (!existing.last_fetched_at || existing.last_fetched_at < topic.last_fetched_at)) {
        existing.last_fetched_at = topic.last_fetched_at;
      }
    } else {
      map.set(topic.name, { ...topic, filters: [...topic.filters] });
    }
  }
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function getPaperTime(paper: AggregatedDataset["papers"][number]): number {
  return (
    Date.parse(paper.collected_at ?? "") ||
    Date.parse(paper.update_date ?? "") ||
    Date.parse(paper.publish_date ?? "") ||
    Date.parse(paper.fetched_at ?? "") ||
    0
  );
}

function mergeDatasets(datasets: AggregatedDataset[]): AggregatedDataset {
  const papers = datasets.flatMap((dataset) => dataset.papers);
  const runs = datasets.flatMap((dataset) => dataset.runs);
  const topics = mergeTopics(datasets.flatMap((dataset) => dataset.topics));

  papers.sort((a, b) => getPaperTime(a) - getPaperTime(b));

  runs.sort((a, b) => {
    const timeA = Date.parse(a.collected_at ?? "") || 0;
    const timeB = Date.parse(b.collected_at ?? "") || 0;
    return timeB - timeA;
  });

  return {
    generated_at: new Date().toISOString(),
    papers,
    runs,
    topics,
  };
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { cache: "no-cache" });
  if (!response.ok) {
    throw new Error(`Failed to load ${url}: ${response.status}`);
  }
  return (await response.json()) as T;
}

function normalizeRange(range: DateRange | null): DateRange | null {
  if (!range || !range.startDate || !range.endDate) {
    return null;
  }
  if (range.startDate <= range.endDate) {
    return range;
  }
  return {
    startDate: range.endDate,
    endDate: range.startDate,
  };
}

function getCutoff(days: RangeOption, anchor?: string): string {
  const base = anchor ? new Date(`${anchor}T00:00:00Z`) : new Date(new Date().setUTCHours(0, 0, 0, 0));
  const cutoff = new Date(base);
  cutoff.setUTCDate(cutoff.getUTCDate() - days);
  return cutoff.toISOString().slice(0, 10);
}

function selectDates(
  availableDates: AvailableDate[],
  selection: RangeSelection,
  customRange: DateRange | null,
): string[] {
  if (selection === "custom") {
    const normalized = normalizeRange(customRange);
    if (!normalized) {
      return [];
    }
    return availableDates
      .map((entry) => entry.date)
      .filter((date) => date >= normalized.startDate && date <= normalized.endDate);
  }

  if (availableDates.length === 0) {
    return [];
  }

  // 基于数据集最新日期计算范围，而非今天
  const latestDate = availableDates.reduce((latest, entry) =>
    entry.date > latest.date ? entry : latest,
  ).date;

  const cutoff = getCutoff(selection, latestDate);
  return availableDates
    .filter((entry) => entry.date >= cutoff)
    .map((entry) => entry.date);
}

export function useDataset(selection: RangeSelection, customRange: DateRange | null): DatasetState {
  const [availableDates, setAvailableDates] = useState<AvailableDate[]>([]);
  const [datesLoading, setDatesLoading] = useState(true);
  const cacheRef = useRef<DatasetCache>({});
  const [data, setData] = useState<AggregatedDataset | null>(null);
  const [datasetLoading, setDatasetLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    async function loadDates(): Promise<void> {
      setDatesLoading(true);
      setError(null);
      try {
        const payload = await fetchJson<AvailableDate[]>("/dataset/dates.json");
        if (active) {
          setAvailableDates(payload);
        }
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : "Unknown error");
          setAvailableDates([]);
        }
      } finally {
        if (active) {
          setDatesLoading(false);
        }
      }
    }

    void loadDates();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (datesLoading) {
      return;
    }

    let active = true;

    async function loadRangeDataset(): Promise<void> {
      setDatasetLoading(true);
      setError(null);
      const selectedDates = selectDates(availableDates, selection, customRange);

      if (selectedDates.length === 0) {
        if (active) {
          setData(null);
          setDatasetLoading(false);
        }
        return;
      }

      try {
        const nextCache: DatasetCache = { ...cacheRef.current };
        const missingDates = selectedDates.filter((date) => !nextCache[date]);
        if (missingDates.length > 0) {
          const fetched = await Promise.all(
            missingDates.map(async (date) => {
              const payload = await fetchJson<AggregatedDataset>(`/dataset/by-date/${date}.json`);
              return { date, payload };
            }),
          );
          if (!active) {
            return;
          }
          for (const item of fetched) {
            nextCache[item.date] = item.payload;
          }
          cacheRef.current = nextCache;
        } else {
          cacheRef.current = nextCache;
        }

        const datasets = selectedDates
          .map((date) => cacheRef.current[date])
          .filter((value): value is AggregatedDataset => Boolean(value));

        if (!active) {
          return;
        }

        setData(datasets.length > 0 ? mergeDatasets(datasets) : null);
      } catch (err) {
        if (active) {
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (active) {
          setDatasetLoading(false);
        }
      }
    }

    void loadRangeDataset();

    return () => {
      active = false;
    };
  }, [availableDates, selection, customRange, datesLoading]);

  const loading = useMemo(() => datesLoading || datasetLoading, [datesLoading, datasetLoading]);

  return {
    data,
    loading,
    error,
    availableDates,
  };
}
